import { AuthContext } from '@core/shared';
import { AccountInvite, AccountInviteStatus, OptionsApi } from '@core/swagger-client';
import { Button, Close, ContainerDefault, ErrorMessagePage, Heading, LoaderPage, Resend } from '@core/ui';
import { BackLink, Link } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useContext } from 'react';
import { getI18n, Trans, useTranslation } from 'react-i18next';

export const ManageAccountInvitesPage = () => {
    const canInviteAccounts = useContext(AuthContext).canInviteAccounts;
    if (!canInviteAccounts) {
        return <ErrorMessagePage statusCode={404} />;
    }

    const { t } = useTranslation();
    const { data: invites, isLoading, error: invitesError, refetch: refreshInvites } = useQuery(OptionsApi.accountInvites.get({ refetchInterval: 15000 }));

    const deleteAccountInviteMutation = useMutation(OptionsApi.accountInvites.remove());
    const deleteAccountInvite = (invite: AccountInvite) => {
        deleteAccountInviteMutation.mutate([invite.emailAddress], {
            onSuccess: () => {
                refreshInvites();
            }
        });
    };

    const resetAccountInviteMutation = useMutation(OptionsApi.accountInvites.reset());
    const resetAccountInvite = (invite: AccountInvite) => {
        resetAccountInviteMutation.mutate([invite.emailAddress], {
            onSuccess: () => {
                refreshInvites();
            }
        });
    };

    if (invitesError) {
        return <ErrorMessagePage apiError={invitesError} />;
    }

    if (isLoading || !invites) {
        return <LoaderPage />;
    }

    return (
        <ContainerDefault>
            <BackLink to="/manage">{t('Button.BackToManageEnv')}</BackLink>
            <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                {t('AccountInvites.PageHeading')}
            </Heading>
            <Link to="/manage/account-invites/import" variant="primary">
                {t('Invites.Button.Import')}
            </Link>

            {invites.length > 0 && (
                <>
                    <Heading variant="h2" classNames="mt-10 2xl:mt-12 mb-4 lg:mb-6 2xl:mb-8">
                        {t('Invites.Heading.ImportedParticipants')}
                    </Heading>

                    <div className="table-responsive bg-white rounded-lg mt-4 shadow-md">
                        <table className="table table-striped mt-2">
                            <thead>
                                <tr>
                                    <th>{t('Account.Label.Email')}</th>
                                    <th>{t('Account.Label.Nickname')}</th>
                                    <th>{t('Account.Label.FirstName')}</th>
                                    <th>{t('Account.Label.LastName')}</th>
                                    <th>{t('Account.Label.CompanyName')}</th>
                                    <th>{t('Invites.Table.Header.InvitedAt')}</th>
                                    <th>{t('Invites.Table.Header.Status')}</th>
                                    <th className="w-10"></th>
                                    <th className="w-10"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {invites.map((invite, rowIndex) => (
                                    <tr key={rowIndex}>
                                        <td>{invite.emailAddress}</td>
                                        <td>{invite.nickname}</td>
                                        <td>{invite.firstName}</td>
                                        <td>{invite.lastName}</td>
                                        <td>{invite.companyName}</td>
                                        <td>
                                            {DateTime.fromISO(invite.createdAt).toLocal().setLocale(getI18n().language).toLocaleString(DateTime.DATETIME_SHORT)}
                                        </td>
                                        <td>
                                            {AccountInviteStatus[invite.status]}
                                            {invite.status === AccountInviteStatus.Error && <span>{invite.errorMessage}</span>}
                                        </td>
                                        <td>
                                            <Button
                                                onClick={() => resetAccountInvite(invite)}
                                                iconbtn
                                                classNames="-my-4 translate-y-1.25"
                                                isLoading={deleteAccountInviteMutation.isPending}
                                            >
                                                <Resend classNames="size-6 stroke-primary" />
                                                <span className="sr-only">
                                                    <Trans values={{ email: invite.emailAddress }} i18nKey="Invites.Button.ResetInvite" />
                                                </span>
                                            </Button>
                                        </td>
                                        <td>
                                            <Button
                                                onClick={() => deleteAccountInvite(invite)}
                                                iconbtn
                                                classNames="-my-4 translate-y-1.25"
                                                isLoading={deleteAccountInviteMutation.isPending}
                                            >
                                                <Close classNames="size-6 stroke-warning" />
                                                <span className="sr-only">
                                                    <Trans values={{ email: invite.emailAddress }} i18nKey="Invites.Button.DeleteParticipant" />
                                                </span>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </ContainerDefault>
    );
};
